import { Affix } from '@warp-ds/react';
import { TextField } from '@warp-ds/react/components/textfield';
import { type ChangeEvent, type FocusEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import type { NumberRange } from '@/interfaces';

const initRange = (vals?: NumberRange): NumberRange => [vals?.[0] ?? null, vals?.[1] ?? null];
const normalizeRange = ([f, t]: NumberRange): NumberRange => {
    if (f == null && t == null) return [f, t];
    if (f == null) return [0, t];
    if (t == null) return [f, 100_000];
    return f <= t ? [f, t] : [t, f];
};
const rangesEqual = (a: NumberRange, b: NumberRange) => a[0] === b[0] && a[1] === b[1];

type RangeInputProps = {
    id: string;
    label: string;
    value: number | null;
    affix: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
};

const PriceRangeInput = ({ id, label, value, affix, onChange, onBlur, onKeyDown }: RangeInputProps) => (
    <>
        <label htmlFor={id} className="sr-only">
            {label}
        </label>
        <TextField
            id={id}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            enterKeyHint="search"
            maxLength={6}
            value={value === null ? '' : value.toString()}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            placeholder="Alle priser"
            helpText={label}>
            <Affix suffix label={affix} />
        </TextField>
    </>
);

type PriceRangeFilterProps = {
    id: string;
    legend: string;
    labels: [string, string];
    affix: string;
    range?: NumberRange;
    updateRange: (range?: NumberRange) => void;
};

export const PriceRangeFilter = ({ id, legend, labels, affix, range: actualRange, updateRange }: PriceRangeFilterProps) => {
    const [range, setRange] = useState<NumberRange>(initRange(actualRange));

    const lastAppliedRef = useRef<NumberRange>(initRange(actualRange));
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const idBase = useId();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Add 'range' to dependencies and verify?
    useEffect(() => {
        const next = initRange(actualRange);
        if (!rangesEqual(next, range)) {
            setRange(next);
            lastAppliedRef.current = next;
        }
    }, [actualRange]);

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        [],
    );

    const applyNow = (candidate: NumberRange) => {
        const normalized = normalizeRange(candidate);
        if (rangesEqual(normalized, lastAppliedRef.current)) return;
        if (rangesEqual(normalized, initRange(actualRange))) return;

        lastAppliedRef.current = normalized;

        updateRange(normalized[0] == null && normalized[1] == null ? undefined : normalized);
    };

    const handleChange = (index: 0 | 1) => (e: ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, '');
        const rangeValue = digits === '' ? null : Number(digits);

        setRange((prev) => {
            if (prev[index] === rangeValue) return prev;
            const next: NumberRange = index === 0 ? [rangeValue, prev[1]] : [prev[0], rangeValue];

            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => applyNow(next), 1000);

            return next;
        });
    };

    const handleBlur = (index: 0 | 1) => (e: FocusEvent<HTMLInputElement>) => {
        const digits = e.currentTarget.value.replace(/\D/g, '');
        const n = digits === '' ? null : Number(digits);

        const next: NumberRange = index === 0 ? [n, range[1]] : [range[0], n];

        if (debounceRef.current) clearTimeout(debounceRef.current);
        applyNow(next);
    };

    const handleKeyDown = (index: 0 | 1) => (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const n = Number((e.currentTarget as HTMLInputElement).value.replace(/\D/g, ''));
        const next: NumberRange = index === 0 ? [n, range[1]] : [range[0], n];
        if (debounceRef.current) clearTimeout(debounceRef.current);
        applyNow(next);
    };

    return (
        <fieldset className="flex items-end gap-8" data-testid={`${id}-rangeFilter`}>
            <legend className="sr-only">{legend}</legend>
            <PriceRangeInput
                id={`${idBase}-rangeFilter-0`}
                label={labels[0]}
                value={range[0]}
                affix={affix}
                onChange={handleChange(0)}
                onBlur={handleBlur(0)}
                onKeyDown={handleKeyDown(0)}
            />
            <PriceRangeInput
                id={`${idBase}-rangeFilter-1`}
                label={labels[1]}
                value={range[1]}
                affix={affix}
                onChange={handleChange(1)}
                onBlur={handleBlur(1)}
                onKeyDown={handleKeyDown(1)}
            />
        </fieldset>
    );
};
